import { useCallback, type HTMLAttributes } from 'react';

import { StyledSelectDropdown } from './styles';

import { Paragraph } from '../Paragraph';

export interface Option {
  key: string;
  label: string;
  [key: string]: unknown;
}

interface DropdownProps<T extends Option> extends HTMLAttributes<HTMLDivElement> {
  placeholder?: string;
  label?: string;
  options: T[];
  onOptionSelect: (option: T) => void;
  includeKeyInLabel?: boolean;
  selectPlaceholder?: boolean;
}
function Dropdown<T extends Option>({
  options,
  placeholder,
  label,
  onOptionSelect,
  includeKeyInLabel = false,
  selectPlaceholder = false,
  ...wrapperProps
}: DropdownProps<T>) {
  const selectOption = useCallback(
    (value: string) => {
      const selected = options.find((option) => option.key === value);
      if (!selected) return;
      onOptionSelect(selected);
    },
    [options, onOptionSelect],
  );

  return (
    <div {...wrapperProps}>
      {!!label && <Paragraph size="sm">{label}</Paragraph>}
      <StyledSelectDropdown
        aria-label="Select an option"
        onChange={(event) => selectOption(event.target.value)}
        defaultValue={selectPlaceholder ? 'placeholder' : options[0].key}
      >
        {placeholder && (
          <option value="placeholder" disabled>
            {placeholder}
          </option>
        )}
        {options.map((dropdownOption) => (
          <option
            key={dropdownOption.key}
            value={dropdownOption.key}
            label={includeKeyInLabel ? `${dropdownOption.label} (${dropdownOption.key})` : dropdownOption.label}
            aria-label={dropdownOption.label}
          >
            {dropdownOption.label}
          </option>
        ))}
      </StyledSelectDropdown>
    </div>
  );
}

export { Dropdown };
